import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLanguages } from "@insite/client-framework/Store/Context/ContextSelectors";
import setLanguage from "@insite/client-framework/Store/Context/Handlers/SetLanguage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import CustomGlobe from "@WhiteCap/Icons/CustomGlobe";
import { isPageExcludedFromFrench } from "@WhiteCap/Utils/frenchExcludedPages";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {
    showIcon = "showIcon",
}

const mapStateToProps = (state: ApplicationState) => ({
    languages: getLanguages(state),
    currentLanguage: state.context.session.language,
});

const mapDispatchToProps = {
    setLanguage,
};

export interface LanguageMenuStyles {
    languageWrapper?: InjectableCss;
    languageIcon?: IconPresentationProps;
    languageSelect?: SelectPresentationProps;
    customDiv?: InjectableCss;
    languageNameStyling?: TypographyProps;
    optionStyling?: TypographyProps;
}

export const languageMenuStyles: LanguageMenuStyles = {
    languageWrapper: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    languageIcon: {
        size: 22,
        color: "secondary.contrast",
        src: CustomGlobe,
        css: css`
            margin-right: 10px;
        `
    },
    languageSelect: {
        backgroundColor: "common.accent",
        cssOverrides: {
            formInputWrapper: css`
                width: 135px;
            `,
            inputSelect: css`
                border: none;
                text-transform: uppercase;
            `,
        },
    },
    customDiv: {
        css: css`
            position: relative;
            display: inline-block;
            .dropdown-content {
                display: none;
                text-align: left;
                position: absolute;
                background-color: #fff;
                width: 129px;
                z-index: 10;
                box-shadow: 3px 7px 16px 3px rgba(0, 0, 0, 0.2);
                right: 0;
                top:25px;
            }
            &:hover .dropdown-content {
                display: flex;
                flex-direction: column;
                margin-left: -8px;
            }
        `,
    },
    languageNameStyling: {
        css: css`
            text-transform: uppercase;
            text-align: center;
            cursor: pointer;
            white-space: nowrap;
            color: ${getColor("primary")} !important;
            font-size: 14px !important;
            line-height: 25px;
        `,
    },
    optionStyling: {
        css: css`
            text-transform: uppercase;
            cursor: pointer;
            color: ${getColor("text.main")};
            min-height: 32px;
            display: flex;
            align-items: center;
            padding: 5px 10px 5px 10px;
            font-size:13px;
            text-transform:capitalize;
            @media (max-width: 1280px) {
                font-size: 11px;
            }
            &:hover {
                color: ${getColor("text.main")};
                background:${getColor("secondary")}; 
            }
        `,
    },
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showIcon]: boolean;
    };
}

export const LogoImage = styled.img`
    height: 22px;
`;

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;
export const LanguageMenu: FC<Props> = ({ languages, currentLanguage, fields, setLanguage }) => {
    if (!languages || languages.length <= 1) {
        return null;
    }

    React.useEffect(() => {
        const languagesObject: { [key: string]: string } = {};

        languages.forEach(language => {
            const cleanedLanguageCode = language.languageCode.toLowerCase();
            languagesObject[cleanedLanguageCode] = language.id;
        });

        const languagesJSON = JSON.stringify(languagesObject);
        localStorage.setItem("languages", languagesJSON);
    }, []);

    React.useEffect(() => {
        if (window.location.search.includes("setcontextlanguagecode")) {
            window.history.replaceState({}, "", "/");
        }
    }, []);

    const onChangeLanguage = (languageId: string, languageCode?: string) => {
        const isFrench = (languageCode ?? "").toLowerCase() === "fr-ca";
        const isExcluded = isPageExcludedFromFrench(window.location.pathname);

        // In onChangeLanguage
        if (isFrench && isExcluded) {
            localStorage.setItem("WccLangChangeInProgress", "true");
            window.location.href = "/?setcontextlanguagecode=fr-CA";
            return;
        }

        localStorage.setItem("WccLangChangeInProgress", "true");
        setLanguage({ languageId });
    };

    const styles = useMergeStyles("languageMenu", languageMenuStyles);

    const menuId = "languageMenu";

    const Container: any = ({ children }: any) => <div style={{ maxWidth: 400 }}>{children}</div>;


    return (
        <StyledWrapper {...styles.languageWrapper}>
            {fields.showIcon &&
                (currentLanguage?.imageFilePath ? (
                    <LogoImage src={currentLanguage.imageFilePath} alt="" />
                ) : (
                    <Icon {...styles.languageIcon} />
                ))}
            <VisuallyHidden as="label" htmlFor={menuId} id={`${menuId}-label`}>
                {translate("Language")}
            </VisuallyHidden>
            <Container>
                <StyledWrapper {...languageMenuStyles.customDiv}>
                    <Typography {...languageMenuStyles.languageNameStyling}>{currentLanguage?.description}</Typography>
                    <StyledWrapper className="dropdown-content" id="lang_dropdown_menu">
                        {languages.map(c => (
                            <Typography
                                {...languageMenuStyles.optionStyling}
                                key={c.id}
                                data-lang={c.languageCode}
                                data-lang-id={c.id}
                                onClick={() => onChangeLanguage(c.id, c.languageCode)}
                            >
                                {c.description}
                            </Typography>
                        ))}
                    </StyledWrapper>
                </StyledWrapper>
            </Container>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(LanguageMenu),
    definition: {
        displayName: "Language Menu",
        icon: "language",
        fieldDefinitions: [
            {
                name: fields.showIcon,
                displayName: "Show Icon",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
